import { useCallback, useEffect, useRef, useState } from 'react';

// What a screen reads, as one of three states: pending (neither data nor
// error), loaded (data) or failed (error, the sentence it failed with).
// read is a memoized function returning a promise; a new read — a new day, a
// new range — is a new request.
//
// The answer is kept with the read that produced it and shown only while that
// read is still the current one, so a screen never renders one range's data
// for another, and an answer for a read the screen has left is dropped.
//
// retry asks again from pending, for a screen that failed to load. reload
// asks again behind what is on screen, for a screen that has just saved: it
// keeps showing the old data until the new answer arrives, and rejects when
// the read fails, so the save's own error handling says so. A reload that
// settles after the screen has moved on to another read — a save on one day
// that finishes once another day is open — is dropped like any other stale
// answer, so it can never replace the current data or leave it pending.
//
// Answers can also arrive out of order for the same read: two reloads after
// two saves, the older answering last (#126). Every request takes the next
// generation, and only the newest one's answer is applied. An older answer is
// dropped, success or failure alike, so it can neither put back older data
// nor show an error for a request that has since been superseded; the newest
// reload still rejects when it fails.
export function useLoad(read) {
  const [ result, setResult ] = useState();
  const [ tries, setTries ] = useState(0);
  const latest = useRef(read);
  const generation = useRef(0);

  useEffect(function() {
    latest.current = read;
  }, [ read ]);

  useEffect(function() {
    let current = true;

    generation.current = generation.current + 1;

    const mine = generation.current;

    read().then(function(data) {
      if (current === true && mine === generation.current) {
        setResult({ read, data });
      }
    }, function({ message }) {
      if (current === true && mine === generation.current) {
        setResult({ read, error: message });
      }
    });

    return function() {
      current = false;
    };
  }, [ read, tries ]);

  const retry = useCallback(function() {
    setResult();
    setTries(function(was) {
      return was + 1;
    });
  }, []);

  const reload = useCallback(function() {
    // A reload of a read the screen has left, such as a save's callback that
    // finishes after the next record opened, settles without touching
    // anything: taking a generation would drop the current read's answer and
    // leave the screen pending (#130).
    if (latest.current !== read) {
      return Promise.resolve();
    }

    generation.current = generation.current + 1;

    const mine = generation.current;

    return read().then(function(data) {
      if (latest.current === read && mine === generation.current) {
        setResult({ read, data });
      }
    }, function(failed) {
      if (latest.current === read && mine === generation.current) {
        throw failed;
      }
    });
  }, [ read ]);

  if (result == null || result.read !== read) {
    return { retry, reload };
  }

  return { data: result.data, error: result.error, retry, reload };
}
